import { getCycles } from "@/services/schoolService";
import { useState } from "react";
import { Link } from "react-router-dom";
import PageHeader from "@/components/base/PageHeader";
import Card, { SectionTitle } from "@/components/base/Card";
import StatCard from "@/components/base/StatCard";
import Badge from "@/components/base/Badge";
import Button from "@/components/base/Button";
import Tabs from "@/components/base/Tabs";
import { LoadingState } from "@/components/base/StateViews";
import { useAsync } from "@/hooks/useAsync";
import { usePager } from "@/hooks/usePager";
import Pagination from "@/components/base/Pagination";
import { getCategories, getTransactions, computeTotals, computeByCycle, topExpenses, resolveTxRef } from "@/services/financeService";
import { getCharges, getChargeCategories } from "@/services/chargeService";
import { useAuth } from "@/contexts/AuthContext";
import CycleScopeBanner from "@/components/feature/CycleScopeBanner";
import { formatMoney, formatDate, plural } from "@/utils/format";
import type { Transaction } from "@/types";
import { cycleIdOf } from "@/utils/cycleScope";

async function load() {
  const [transactions, categories, cycles, charges, chargeCategories] = await Promise.all([
    getTransactions(),
    getCategories(),
    getCycles(),
    getCharges(),
    getChargeCategories(),
  ]);
  return { transactions, categories, cycles, charges, chargeCategories };
}

export default function FinancePage() {
  const { allowedCycleIds } = useAuth();
  const { data, loading } = useAsync(load, []);
  const [tab, setTab] = useState("all");
  const [cycleFilter, setCycleFilter] = useState("all");
  const [search, setSearch] = useState("");
  const pager = usePager();

  if (loading || !data) return <LoadingState label="Chargement des finances..." />;
  const { transactions, categories, cycles, charges, chargeCategories } = data;
  const primaireId = cycleIdOf(cycles, "PRIMAIRE");

  const visibleCycles = cycles.filter((c) => allowedCycleIds.includes(c.id));
  const scopedTransactions = transactions.filter((t) => allowedCycleIds.includes(t.cycleId));

  const refs = { categories, chargeCategories, charges };
  const txRef = (t: Transaction) => resolveTxRef(t, refs);
  const cycleLabel = (id: string) => cycles.find((c) => c.id === id)?.label ?? "—";

  const totals = computeTotals(scopedTransactions);
  const balances = computeByCycle(scopedTransactions, visibleCycles.map((c) => c.id));
  const top = topExpenses(scopedTransactions, 5);

  const filtered = scopedTransactions
    .filter((t) => tab === "all" || t.type === tab)
    .filter((t) => cycleFilter === "all" || t.cycleId === cycleFilter)
    .filter((t) => {
      const q = search.trim().toLowerCase();
      if (!q) return true;
      return t.description.toLowerCase().includes(q) || txRef(t).label.toLowerCase().includes(q);
    })
    .sort((a, b) => b.date.localeCompare(a.date));
  const page = pager.slice(filtered, `${tab}|${cycleFilter}|${search}`);

  return (
    <div className="animate-fade-in">
      <PageHeader
        title="Gestion financière"
        subtitle="Tout est géré séparément par cycle — chaque cycle a son propre budget"
        icon="ri-wallet-3-line"
        actions={
          <>
            <Link to="/dashboard/revenue">
              <Button variant="outline" icon="ri-arrow-up-circle-line">
                Recettes
              </Button>
            </Link>
            <Link to="/dashboard/expenses">
              <Button icon="ri-arrow-down-circle-line">Dépenses</Button>
            </Link>
          </>
        }
      />

      <CycleScopeBanner className="mb-5" />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Caisse disponible" value={formatMoney(totals.solde)} icon="ri-safe-2-line" tone="primary" hint="Solde net global" />
        <StatCard label="Recettes encaissées" value={formatMoney(totals.recettes)} icon="ri-arrow-up-circle-line" tone="secondary" />
        <StatCard label="Dépenses engagées" value={formatMoney(totals.depenses)} icon="ri-arrow-down-circle-line" tone="accent" />
        <StatCard
          label="Marge"
          value={`${totals.recettes ? Math.round((totals.solde / totals.recettes) * 100) : 0} %`}
          icon="ri-percent-line"
          tone="primary"
          hint="Part du résultat dans les recettes"
        />
      </div>

      <div className="mt-6 grid grid-cols-1 gap-5 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <Card padded={false}>
            <div className="flex flex-col gap-3 border-b border-background-200 p-4 lg:flex-row lg:items-center lg:justify-between">
              <SectionTitle
                icon="ri-book-3-line"
                title="Journal financier"
                subtitle={`${plural(filtered.length, "opération")}`}
              />
              <div className="flex flex-wrap items-center gap-2">
                <div className="flex items-center gap-2 rounded-md border border-background-300 bg-background-50 px-3 py-1.5">
                  <i className="ri-search-line text-foreground-400 text-sm"></i>
                  <input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Rechercher..."
                    className="w-36 bg-transparent text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none"
                  />
                </div>
                <select
                  value={cycleFilter}
                  onChange={(e) => setCycleFilter(e.target.value)}
                  className="rounded-md border border-background-300 bg-background-50 px-3 py-1.5 text-sm cursor-pointer focus:outline-none"
                >
                  <option value="all">Tous les cycles</option>
                  {visibleCycles.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="px-4 pt-4">
              <Tabs
                tabs={[
                  { id: "all", label: "Tout" },
                  { id: "recette", label: "Recettes", icon: "ri-arrow-up-line" },
                  { id: "depense", label: "Dépenses", icon: "ri-arrow-down-line" },
                ]}
                active={tab}
                onChange={setTab}
              />
            </div>
            <div className="mt-4 overflow-x-auto">
              <table className="w-full min-w-[680px] text-sm">
                <thead>
                  <tr className="border-y border-background-200 text-left text-xs uppercase tracking-wide text-foreground-500">
                    <th className="px-4 py-2.5 font-medium">Date</th>
                    <th className="px-4 py-2.5 font-medium">Catégorie</th>
                    <th className="px-4 py-2.5 font-medium">Cycle</th>
                    <th className="px-4 py-2.5 font-medium">Description</th>
                    <th className="px-4 py-2.5 font-medium text-right">Montant</th>
                  </tr>
                </thead>
                <tbody>
                  {page.items.map((t) => (
                    <tr key={t.id} className="border-b border-background-200 last:border-0 hover:bg-background-100/60 transition-colors">
                      <td className="px-4 py-3 text-xs text-foreground-500 whitespace-nowrap">{formatDate(t.date)}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <span
                            className={`w-7 h-7 rounded-md flex items-center justify-center shrink-0 ${
                              t.type === "recette" ? "bg-primary-50 text-primary-700" : "bg-accent-50 text-accent-700"
                            }`}
                          >
                            <i className={`${txRef(t).icon} text-sm`}></i>
                          </span>
                          <span className="text-foreground-800">{txRef(t).label}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <Badge tone={t.cycleId === primaireId ? "warning" : "secondary"}>{cycleLabel(t.cycleId)}</Badge>
                      </td>
                      <td className="px-4 py-3 text-xs text-foreground-600 max-w-[240px] truncate">{t.description}</td>
                      <td className={`px-4 py-3 text-right font-medium whitespace-nowrap ${t.type === "recette" ? "text-primary-700" : "text-accent-700"}`}>
                        {t.type === "recette" ? "+" : "−"} {formatMoney(t.amount)}
                      </td>
                    </tr>
                  ))}
                  {filtered.length === 0 && (
                    <tr>
                      <td colSpan={5} className="px-4 py-10 text-center text-sm text-foreground-400">
                        Aucune opération pour ces filtres.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <Pagination pager={page} className="border-t border-background-200" />
          </Card>
        </div>

        <div className="space-y-5">
          <Card>
            <SectionTitle icon="ri-flag-2-line" title="Bilan par cycle" subtitle="Excédent ou déficit" />
            <div className="space-y-3">
              {balances.map((b) => (
                <div key={b.cycleId} className="rounded-md border border-background-200 bg-background-100/50 p-3.5">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-foreground-900">{cycleLabel(b.cycleId)}</span>
                    <Badge tone={b.solde >= 0 ? "success" : "danger"}>{b.solde >= 0 ? "Excédent" : "Déficit"}</Badge>
                  </div>
                  <p className={`mt-1.5 text-base font-semibold font-heading ${b.solde >= 0 ? "text-primary-700" : "text-accent-700"}`}>
                    {formatMoney(b.solde)}
                  </p>
                  <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
                    <span className="text-foreground-500">Recettes: <span className="text-foreground-800">{formatMoney(b.recettes)}</span></span>
                    <span className="text-foreground-500">Dépenses: <span className="text-foreground-800">{formatMoney(b.depenses)}</span></span>
                  </div>
                </div>
              ))}
            </div>
          </Card>

          <Card>
            <SectionTitle icon="ri-fire-line" title="Principales dépenses" subtitle="Les 5 postes les plus lourds" />
            <div className="space-y-2">
              {top.map((t) => (
                <div key={t.id} className="flex items-center justify-between gap-2 rounded-md px-2 py-2 hover:bg-background-100 transition-colors">
                  <div className="min-w-0">
                    <p className="text-sm text-foreground-800 truncate">{txRef(t).label}</p>
                    <p className="text-xs text-foreground-500">{cycleLabel(t.cycleId)} • {formatDate(t.date)}</p>
                  </div>
                  <span className="text-sm font-medium text-accent-700 whitespace-nowrap">{formatMoney(t.amount)}</span>
                </div>
              ))}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}